// EventHandlers.js - Functions for handling calendar events

import axios from 'axios';
import { isPastDate } from './EventUtils';

// Handle event drop (drag and drop)
export const handleEventDrop = async (dropInfo, currentUser, googleCalendarEnabled, showToast, fetchEvents) => {
    const { event } = dropInfo;

    if (isPastDate(event.start)) {
        showToast('warn', 'Not Allowed', 'Cannot move events to dates before today');
        dropInfo.revert();
        return;
    }

    // Get userId from either extendedProps or directly from the event
    const eventUserId = event.extendedProps.userId || event._def.extendedProps.userId;

    // Check if this event belongs to the partner (not the current user)
    if (eventUserId && eventUserId !== currentUser.uid) {
        showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
        dropInfo.revert();
        return;
    }

    if (event.id.toString().startsWith('todo-')) {
        // Todo events are handled differently
        return;
    } else {
        try {
            if (event.id.toString().startsWith('generated-')) {
                showToast('warn', 'Warning', 'Cannot move a recurring event instance');
                dropInfo.revert();
                return;
            }

            const startDate = event.start;
            const endDate = event.end || new Date(startDate.getTime() + 3600000);

            // Update the event using the save-event endpoint
            const response = await axios.post('https://api.frazza.online/calendar/save-event', {
                userId: currentUser.uid,
                eventData: {
                    id: event.id,
                    title: event.title,
                    start: startDate,
                    end: endDate,
                    allDay: event.allDay,
                    description: event.extendedProps?.description || '',
                    backgroundColor: event.backgroundColor,
                    googleEventId: event.extendedProps?.googleEventId
                }
            });

            if (response.data.success) {
                showToast('success', 'Success', 'Event time updated');

                // Real-time listener will update the calendar automatically
                // No need to call fetchEvents()

                // If Google Calendar is enabled, trigger a sync
                if (googleCalendarEnabled) {
                    try {
                        // Sync in background without showing toast
                        axios.post('https://api.frazza.online/calendar/sync', {
                            userId: currentUser.uid
                        }).catch(error => {
                            console.error("Error syncing after event drop:", error);
                        });
                    } catch (syncError) {
                        console.error("Error initiating sync after event drop:", syncError);
                    }
                }
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to update event time');
                dropInfo.revert();
            }
        } catch (error) {
            console.error("Error updating event:", error);
            showToast('error', 'Error', `Failed to update event time: ${error.response?.data?.error || error.message}`);
            dropInfo.revert();
        }
    }
};

// Handle event resize
export const handleEventResize = async (resizeInfo, currentUser, googleCalendarEnabled, showToast, fetchEvents) => {
    const { event } = resizeInfo;

    // Check if resized to include a date in the past
    if (isPastDate(event.start)) {
        showToast('warn', 'Not Allowed', 'Cannot resize events to dates before today');
        resizeInfo.revert();
        return;
    }

    // Get userId from either extendedProps or directly from the event
    const eventUserId = event.extendedProps.userId || event._def.extendedProps.userId;

    // Check if this event belongs to the partner (not the current user)
    if (eventUserId && eventUserId !== currentUser.uid) {
        showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
        resizeInfo.revert();
        return;
    }

    try {
        if (event.id.toString().startsWith('generated-')) {
            showToast('warn', 'Warning', 'Cannot resize a recurring event instance');
            resizeInfo.revert();
            return;
        }

        if (event.id.toString().startsWith('todo-')) {
            // Todo events are handled differently
            return;
        } else {
            // Update the event using the save-event endpoint
            const response = await axios.post('https://api.frazza.online/calendar/save-event', {
                userId: currentUser.uid,
                eventData: {
                    id: event.id,
                    title: event.title,
                    start: event.start,
                    end: event.end,
                    allDay: event.allDay,
                    description: event.extendedProps?.description || '',
                    backgroundColor: event.backgroundColor,
                    googleEventId: event.extendedProps?.googleEventId
                }
            });

            if (response.data.success) {
                showToast('success', 'Success', 'Event duration updated');

                // Real-time listener will update the calendar automatically
                // No need to call fetchEvents()

                // If Google Calendar is enabled, trigger a sync
                if (googleCalendarEnabled) {
                    try {
                        // Sync in background without showing toast
                        axios.post('https://api.frazza.online/calendar/sync', {
                            userId: currentUser.uid
                        }).catch(error => {
                            console.error("Error syncing after event resize:", error);
                        });
                    } catch (syncError) {
                        console.error("Error initiating sync after event resize:", syncError);
                    }
                }
            } else {
                showToast('error', 'Error', response.data.message || 'Failed to update event duration');
                resizeInfo.revert();
            }
        }
    } catch (error) {
        console.error("Error updating event duration:", error);
        showToast('error', 'Error', `Failed to update event duration: ${error.response?.data?.error || error.message}`);
        resizeInfo.revert();
    }
};

// Handle event click
export const handleEventClick = (info, currentUser, setSelectedEvent, showToast, setVisitData, setVisitViewMode, setVisitDialog, setEvent, setEventDialog) => {
    if (info.event.id.toString().startsWith('todo-')) {
        showToast('info', 'Info', 'Todos cannot be edited from the calendar');
        return;
    }

    setSelectedEvent(info.event);

    // Get userId from either extendedProps or directly from the event
    const eventUserId = info.event.extendedProps.userId || info.event._def.extendedProps.userId;

    // Check if this event belongs to the partner (not the current user)
    if (eventUserId && eventUserId !== currentUser.uid) {
        showToast('warn', 'Not Allowed', 'You cannot edit or delete your partner\'s events');
        return;
    }

    // Check if this is a visit event (has visitDetails in extendedProps)
    if (info.event.extendedProps.visitDetails) {
        // This is a visit, prepare visit data
        const visitData = {
            id: info.event.id,
            title: info.event.title,
            arrival: info.event.start,
            departure: info.event.end || info.event.start,
            allDay: info.event.allDay,
            description: info.event.extendedProps.description || '',
            backgroundColor: info.event.backgroundColor,
            linkedTodoId: info.event.extendedProps.linkedTodoId,
            repeat: info.event.extendedProps.repeat || false,
            repeatType: info.event.extendedProps.repeatType || 'daily',
            repeatUntil: info.event.extendedProps.repeatUntil,
            parentEventId: info.event.extendedProps.parentEventId,
            // Visit specific fields
            reminders: info.event.extendedProps.visitDetails.reminders || {
                oneWeekBefore: false,
                twoDaysBefore: false
            },
            dailyPlans: info.event.extendedProps.visitDetails.dailyPlans || [],
            tickets: info.event.extendedProps.visitDetails.tickets || []
        };

        setVisitData(visitData);
        setVisitViewMode(true);
        setVisitDialog(true);
    } else {
        // Regular event
        const eventData = {
            id: info.event.id,
            title: info.event.title,
            start: info.event.start,
            end: info.event.end || info.event.start,
            allDay: info.event.allDay,
            description: info.event.extendedProps.description || '',
            backgroundColor: info.event.backgroundColor,
            linkedTodoId: info.event.extendedProps.linkedTodoId,
            repeat: info.event.extendedProps.repeat || false,
            repeatType: info.event.extendedProps.repeatType || 'daily',
            repeatUntil: info.event.extendedProps.repeatUntil,
            parentEventId: info.event.extendedProps.parentEventId
        };
        setEvent(eventData);
        setEventDialog(true);
    }
};

// Handle date select
export const handleDateSelect = (selectInfo, setEvent, setEventDialog) => {
    // Create a new event starting at the selected date
    const start = selectInfo.startStr ? new Date(selectInfo.startStr) : new Date();

    // Set end date one hour later if it's not an all-day selection
    let end;
    if (selectInfo.allDay) {
        end = selectInfo.endStr ? new Date(selectInfo.endStr) : new Date(start);
    } else {
        end = new Date(start);
        end.setHours(end.getHours() + 1);
    }

    setEvent({
        id: null,
        title: '',
        start: start,
        end: end,
        allDay: selectInfo.allDay,
        description: '',
        backgroundColor: '#1976D2',
        linkedTodoId: null,
        repeat: false,
        repeatType: 'daily',
        repeatUntil: null,
        parentEventId: null,
    });

    setEventDialog(true);
};

// Delete event from Google Calendar
export const deleteEventFromGoogleCalendar = async (googleEventId, currentUser) => {
    try {
        if (!googleEventId) {
            return false;
        }

        const response = await axios.delete(`https://api.frazza.online/calendar/google-event/${googleEventId}`, {
            params: { userId: currentUser.uid }
        });

        return response.data.success;
    } catch (error) {
        console.error("Error deleting from Google Calendar:", error);
        return false;
    }
};